import { useEffect, useRef, useState } from 'react';
import {
  Plus,
  Server,
  Trash2,
  TestTube,
  FileSpreadsheet,
  Globe,
  CloudDownload,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronUp,
  Download,
} from 'lucide-react';
import {
  useCreateServer,
  useDeleteServer,
  useImportServersFile,
  useImportServersUrl,
  useServers,
  useSyncConfig,
  useTestServer,
  useTriggerSheetSync,
  type ImportResult,
  type PresenceFilter,
} from '@/hooks/useServers';
import { Modal } from '@/components/Modal';
import { EmptyState } from '@/components/EmptyState';
import { ApiError } from '@/lib/api';

interface FlashMessage {
  tone: 'ok' | 'warn' | 'error';
  text: string;
}

export function ServersPage() {
  // Polling 5s for 30s after a fresh import so the table reflects sync progress.
  const [pollUntil, setPollUntil] = useState<number>(0);
  const refetchInterval = pollUntil > Date.now() ? 5000 : false;

  const [presence, setPresence] = useState<PresenceFilter>('');
  const list = useServers({ refetchInterval, presence });
  const syncConfig = useSyncConfig();
  const test = useTestServer();
  const del = useDeleteServer();

  const [showCreate, setShowCreate] = useState(false);
  const [showImportUrl, setShowImportUrl] = useState(false);
  const [flash, setFlash] = useState<FlashMessage | null>(null);
  const [lastImport, setLastImport] = useState<ImportResult | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const importFile = useImportServersFile();
  const importUrl = useImportServersUrl();
  const triggerSheet = useTriggerSheetSync();

  function startPolling() {
    setPollUntil(Date.now() + 30_000);
  }

  function showResult(r: ImportResult) {
    setLastImport(r);
    setShowErrors(false);
    const parts = [
      `${r.imported_new} nuevos`,
      `${r.imported_updated} actualizados`,
      `${r.skipped} sin cambios`,
    ];
    if (r.skipped_no_datacole > 0) parts.push(`${r.skipped_no_datacole} sin Datacole (ignorados)`);
    if (r.errors.length > 0) parts.push(`${r.errors.length} errores`);
    parts.push(`sincronizando ${r.enqueued}…`);
    setFlash({
      tone: r.errors.length > 0 ? 'warn' : 'ok',
      text: `Importados ${parts.join(' · ')}`,
    });
    if (r.enqueued > 0) startPolling();
  }

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const r = await importFile.mutateAsync(file);
      showResult(r);
    } catch (err) {
      setFlash({
        tone: 'error',
        text: err instanceof ApiError ? `Error ${err.status}: ${err.message}` : 'Error subiendo archivo',
      });
    }
  }

  async function onTest(id: string, name: string) {
    try {
      const r = await test.mutateAsync(id);
      setFlash({ tone: r.ok ? 'ok' : 'error', text: `${name}: ${r.message}` });
    } catch (err) {
      const msg =
        err instanceof ApiError && err.body && typeof err.body === 'object' && 'message' in err.body
          ? String((err.body as { message: unknown }).message)
          : 'inaccesible';
      setFlash({ tone: 'error', text: `${name}: ${msg}` });
    }
  }

  async function onSyncSheet() {
    try {
      await triggerSheet.mutateAsync();
      setFlash({
        tone: 'ok',
        text: 'Sync con Google Sheet encolado. La tabla se actualiza en breve…',
      });
      startPolling();
    } catch (err) {
      const msg =
        err instanceof ApiError && err.body && typeof err.body === 'object' && 'message' in err.body
          ? String((err.body as { message: unknown }).message)
          : err instanceof Error
            ? err.message
            : 'Error';
      setFlash({ tone: 'error', text: msg });
    }
  }

  const sheetCard = syncConfig.data?.sheet_sync_enabled;
  const sheetLastRun = syncConfig.data?.last_run_at
    ? `Último sync: ${new Date(syncConfig.data.last_run_at).toLocaleString()}`
    : 'Nunca ejecutado';
  const sheetSubtitle = sheetCard
    ? `Cada ${syncConfig.data!.sheet_sync_interval_hours}h auto · ${sheetLastRun}`
    : 'No configurado en .env (DATACOLE_SHEET_CSV_URL)';

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-moss-200">Servidores Datacole</h1>
          <p className="mt-1 text-sm text-moss-300/70">
            Cada servidor expone <code className="text-mustard-300">/central_api/*</code>.
          </p>
        </div>
      </div>

      {/* --- 4 import cards --- */}
      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <ImportCard
          icon={CloudDownload}
          title="Sync Google Sheet"
          subtitle={sheetSubtitle}
          loading={triggerSheet.isPending}
          onClick={onSyncSheet}
          disabled={!sheetCard}
          highlight={sheetCard}
        />
        <ImportCard
          icon={FileSpreadsheet}
          title="Importar Excel / CSV"
          subtitle="Sube .xlsx o .csv con columna 'dominio'"
          loading={importFile.isPending}
          onClick={() => fileInputRef.current?.click()}
        />
        <ImportCard
          icon={Globe}
          title="Importar desde URL"
          subtitle="JSON o CSV público"
          loading={importUrl.isPending}
          onClick={() => setShowImportUrl(true)}
        />
        <ImportCard
          icon={Plus}
          title="Agregar manual"
          subtitle="Un servidor a la vez"
          onClick={() => setShowCreate(true)}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={onPickFile}
        />
      </section>

      {/* Downloadable templates — discoverable hint right under the cards. */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-moss-300/70">
        <span>
          Plantilla — una sola columna <code className="text-mustard-300">dominio</code>:
        </span>
        <TemplateLink format="xlsx" label="Excel" />
        <TemplateLink format="csv" label="CSV" />
      </div>

      {/* --- flash banner --- */}
      {flash && (
        <div
          className={`glass-card flex items-start gap-3 border p-4 text-sm ${
            flash.tone === 'ok'
              ? 'border-ok/40 bg-ok/10 text-moss-200'
              : flash.tone === 'warn'
                ? 'border-warn/40 bg-warn/10 text-moss-200'
                : 'border-danger/40 bg-danger/10 text-moss-200'
          }`}
        >
          {flash.tone === 'ok' ? (
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-ok" />
          ) : (
            <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-danger" />
          )}
          <div className="flex-1">
            <div>{flash.text}</div>
            {lastImport && lastImport.errors.length > 0 && (
              <button
                type="button"
                className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-mustard-300 hover:text-mustard-400"
                onClick={() => setShowErrors((v) => !v)}
              >
                {showErrors ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                {showErrors ? 'Ocultar' : 'Ver'} {lastImport.errors.length} error
                {lastImport.errors.length === 1 ? '' : 'es'}
              </button>
            )}
          </div>
          <button
            type="button"
            className="text-xs text-moss-300/60 hover:text-moss-200"
            onClick={() => setFlash(null)}
          >
            ✕
          </button>
        </div>
      )}

      {showErrors && lastImport && lastImport.errors.length > 0 && (
        <div className="glass-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-ink-700/70 bg-ink-800/40 text-left text-xs uppercase tracking-wide text-moss-300/70">
              <tr>
                <th className="px-4 py-2">Fila</th>
                <th className="px-4 py-2">Dominio</th>
                <th className="px-4 py-2">Error</th>
              </tr>
            </thead>
            <tbody>
              {lastImport.errors.map((e, i) => (
                <tr key={i} className="border-b border-ink-700/40 last:border-0">
                  <td className="px-4 py-2 font-mono text-moss-300">{e.row}</td>
                  <td className="px-4 py-2 font-mono text-xs text-moss-300">{e.dominio}</td>
                  <td className="px-4 py-2 text-danger">{e.msg}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* --- presence filter --- */}
      <div className="glass-card flex flex-wrap items-center gap-3 p-3">
        <span className="text-xs uppercase tracking-wide text-moss-300/70">Filtrar:</span>
        <PresenceTab label="Todos" value="" current={presence} setValue={setPresence} />
        <PresenceTab label="En sheet" value="in_sheet" current={presence} setValue={setPresence} />
        <PresenceTab
          label="No en sheet"
          value="not_in_sheet"
          current={presence}
          setValue={setPresence}
        />
        <span className="ml-auto text-xs text-moss-300/60">
          {list.data?.rows.length ?? 0} resultados
        </span>
      </div>

      {/* --- table --- */}
      {list.data?.rows.length === 0 ? (
        <EmptyState
          icon={Server}
          title={presence ? 'Sin servidores con ese filtro' : 'Aún no hay servidores'}
          description={
            presence
              ? 'Cambia el filtro o importa nuevos dominios.'
              : 'Configura DATACOLE_SHEET_CSV_URL y sincroniza, o importa un Excel/CSV manualmente.'
          }
          action={
            !presence && (
              <button className="btn-primary" onClick={() => fileInputRef.current?.click()}>
                <FileSpreadsheet className="h-4 w-4" /> Importar archivo
              </button>
            )
          }
        />
      ) : (
        <div className="glass-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-ink-700/70 bg-ink-800/40 text-left text-xs uppercase tracking-wide text-moss-300/70">
              <tr>
                <th className="px-4 py-3">Nombre</th>
                <th className="px-4 py-3">Base URL</th>
                <th className="px-4 py-3 text-right">Colegios</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">En sheet</th>
                <th className="px-4 py-3">Último sync</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {list.data?.rows.map((s) => (
                <tr
                  key={s.id}
                  className="border-b border-ink-700/40 last:border-0 hover:bg-ink-700/30"
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-moss-200">{s.name}</div>
                    {s.notes && <div className="text-[11px] text-moss-300/60">{s.notes}</div>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-moss-300">{s.base_url}</td>
                  <td className="px-4 py-3 text-right font-mono text-moss-200">
                    {s.clients_count}
                  </td>
                  <td className="px-4 py-3">
                    {s.last_sync_status === 'ok' ? (
                      <span className="pill-ok">ok</span>
                    ) : s.last_sync_status === 'partial' ? (
                      <span className="pill-warn">parcial</span>
                    ) : s.last_sync_status === 'error' ? (
                      <span className="pill-danger">error</span>
                    ) : (
                      <span className="pill-warn">pendiente</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {s.present_in_sheet ? (
                      <span className="pill-ok">sí</span>
                    ) : (
                      <span
                        className="pill-danger"
                        title="Este dominio dejó de aparecer en el Google Sheet. Decide si lo eliminas."
                      >
                        no
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-moss-300">
                    {s.last_sync_at ? new Date(s.last_sync_at).toLocaleString() : 'nunca'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1.5">
                      <button
                        className="btn-ghost h-8 px-2 text-xs"
                        onClick={() => onTest(s.id, s.name)}
                        disabled={test.isPending}
                      >
                        <TestTube className="mr-1 h-3 w-3" /> Test
                      </button>
                      <button
                        className="btn-ghost h-8 px-2 text-xs hover:border-danger/60 hover:text-danger"
                        onClick={() => {
                          if (confirm(`¿Eliminar "${s.name}" y todos sus colegios cacheados?`)) {
                            del.mutate(s.id);
                          }
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateServerModal open={showCreate} onClose={() => setShowCreate(false)} />
      <ImportUrlModal
        open={showImportUrl}
        onClose={() => setShowImportUrl(false)}
        onResult={showResult}
        run={importUrl.mutateAsync}
        loading={importUrl.isPending}
      />
    </div>
  );
}

function TemplateLink({ format, label }: { format: 'csv' | 'xlsx'; label: string }) {
  // The template endpoint requires auth; trigger the download via a programmatic
  // fetch + Blob so cookies travel with the request.
  async function download() {
    const base = (import.meta.env.VITE_API_URL ?? '/api').replace(/\/$/, '');
    const res = await fetch(`${base}/servers/import-template?format=${format}`, {
      credentials: 'include',
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `servidores-plantilla.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
  return (
    <button
      type="button"
      onClick={download}
      className="inline-flex items-center gap-1 rounded-md border border-ink-600 px-2 py-1 text-xs font-medium text-mustard-300 transition hover:border-mustard-500/60 hover:bg-ink-700/40"
    >
      <Download className="h-3 w-3" />
      {label}
    </button>
  );
}

function PresenceTab({
  label,
  value,
  current,
  setValue,
}: {
  label: string;
  value: PresenceFilter;
  current: PresenceFilter;
  setValue: (v: PresenceFilter) => void;
}) {
  const active = value === current;
  return (
    <button
      type="button"
      onClick={() => setValue(value)}
      className={`rounded-lg px-3 py-1 text-xs font-medium transition ${
        active
          ? 'bg-mustard-500/15 text-mustard-300'
          : 'border border-ink-600 text-moss-300 hover:border-mustard-500/40 hover:text-mustard-300'
      }`}
    >
      {label}
    </button>
  );
}

function ImportCard({
  icon: Icon,
  title,
  subtitle,
  onClick,
  loading,
  disabled,
  highlight,
}: {
  icon: typeof Plus;
  title: string;
  subtitle: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  highlight?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading || disabled}
      className={`glass-card group flex items-center gap-4 p-5 text-left transition ${
        disabled
          ? 'cursor-not-allowed opacity-50'
          : highlight
            ? 'border-mustard-500/40 hover:border-mustard-500/70 hover:bg-ink-700/40'
            : 'hover:border-mustard-500/60 hover:bg-ink-700/40'
      } disabled:cursor-wait`}
    >
      <div
        className={`rounded-2xl p-3 transition ${
          highlight
            ? 'bg-mustard-500/25 text-mustard-300 group-hover:bg-mustard-500/35'
            : 'bg-mustard-500/15 text-mustard-300 group-hover:bg-mustard-500/25'
        }`}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 overflow-hidden">
        <div className="text-sm font-semibold text-moss-200">{title}</div>
        <div className="truncate text-xs text-moss-300/70">{subtitle}</div>
      </div>
      {loading && <span className="text-xs text-moss-300/60">…</span>}
    </button>
  );
}

function CreateServerModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateServer();
  const [baseUrl, setBaseUrl] = useState('');

  function normalize(v: string): string {
    let u = v.trim();
    if (!u) return u;
    if (!/^https?:\/\//i.test(u)) u = `https://${u}`;
    return u.replace(/\/+$/, '');
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await create.mutateAsync({ base_url: normalize(baseUrl) });
      setBaseUrl('');
      onClose();
    } catch {
      /* error shown below */
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Agregar servidor">
      <form className="space-y-4" onSubmit={submit}>
        <Field label="Dominio">
          <input
            required
            autoFocus
            className="input-base font-mono"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="colegio-abc.datacole.com"
          />
        </Field>
        <p className="text-xs text-moss-300/60">
          El nombre se autogenera del host. Si necesitas editarlo después, lo haces desde la tabla.
        </p>
        {create.isError && (
          <div className="text-sm text-danger">{(create.error as Error).message}</div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" className="btn-primary" disabled={create.isPending}>
            {create.isPending ? 'Creando…' : 'Crear'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ImportUrlModal({
  open,
  onClose,
  onResult,
  run,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  onResult: (r: ImportResult) => void;
  run: (url: string) => Promise<ImportResult>;
  loading: boolean;
}) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const r = await run(url);
      onResult(r);
      setUrl('');
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? `${err.status} · ${err.message}` : 'Error inesperado');
    }
  }

  useEffect(() => {
    if (!open) setError(null);
  }, [open]);

  return (
    <Modal open={open} onClose={onClose} title="Importar desde URL">
      <form className="space-y-4" onSubmit={submit}>
        <p className="text-xs text-moss-300/70">
          La URL debe responder un JSON array <code className="text-mustard-300">[{'{'}dominio:…{'}'}]</code>{' '}
          o un CSV con header. Timeout 15s.
        </p>
        <Field label="URL del archivo remoto">
          <input
            required
            type="url"
            className="input-base font-mono text-xs"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://datacole.com/servers.json"
          />
        </Field>
        {error && <div className="text-sm text-danger">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Descargando…' : 'Importar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium uppercase tracking-wide text-moss-300">{label}</label>
      {children}
    </div>
  );
}
